import { useEffect, useRef, useState } from 'react'
import type { NextAction, Readiness } from '../api'

// Actions that LOCK something — the plan, or an offer to a client — and so must
// be asked about before they happen rather than reported afterwards. The CTAs
// said "Lock the plan so it can run" and then did it on the first click, with
// nothing on screen offering a way back (PRINCIPLES #2: never quietly do
// something expensive or irreversible on someone's behalf).
const LOCKING: Record<string, { what: string; verb: string }> = {
  finalize: {
    what: 'Locks the plan so it can run. Tasks and costs stop changing until you reopen the draft.',
    verb: 'Lock the plan',
  },
  propose_bid: {
    what: 'Turns the draft estimate into a proposed contract you can send to the client. The figures stop moving once it goes out.',
    verb: 'Propose it',
  },
}

// PriorityList renders the backend's ranked next-actions as the assistant's "top
// pending priorities" — one card per action, the recommended one elevated, a
// blocked one shown disabled with its gate. Each [do it] calls back to the
// plan's existing trigger for that action id.
export function PriorityList({
  readiness,
  busy,
  onAction,
}: {
  readiness: Readiness | null
  busy: boolean
  onAction: (a: NextAction) => void
}) {
  const [confirming, setConfirming] = useState<string | null>(null)
  // Which action the operator actually launched. `busy` says something is
  // running; it does not say WHAT, and a ladder where every button greys out at
  // once looks broken rather than working. PRINCIPLES #2 — the machine explains
  // itself.
  const [launched, setLaunched] = useState<string | null>(null)
  // Clear only when work FINISHES — busy is still false for the render right
  // after the click, and clearing on "not busy" wiped the label before it ever
  // showed. Track the transition instead.
  const wasBusy = useRef(false)
  useEffect(() => {
    if (wasBusy.current && !busy) setLaunched(null)
    wasBusy.current = busy
  }, [busy])

  // The ladder sits BELOW the transcript, and the transcript is flex:1 — so
  // rendering nothing here and then filling in shrinks the transcript and drags
  // its bottom-anchored content up with it. That is the last layout shift on the
  // page.
  //
  // A first attempt at this reserved the space with EMPTY cards, which collapsed
  // to padding (~28px) against a real card of 128px — so it reserved the wrong
  // height and made the shift worse, not better. The height below is measured
  // off a rendered card, and the skeleton carries the same line count so it
  // tracks the real thing when the copy changes.
  if (!readiness) {
    return (
      <div className="priorities" aria-hidden>
        <div className="priorities-head">
          <span className="priorities-title">Top priorities</span>
        </div>
        <ol className="priorities-list">
          {[0, 1, 2].map((i) => (
            <li key={i} className="priority-card skeleton">
              <span className="priority-step">&nbsp;</span>
              <div className="priority-body">
                <span className="priority-label">&nbsp;</span>
                <span className="priority-why">&nbsp;<br />&nbsp;</span>
              </div>
              <span className="priority-cta">&nbsp;</span>
            </li>
          ))}
        </ol>
      </div>
    )
  }
  if (readiness.actions.length === 0) {
    // Nothing actionable — surface the implied "ready" state rather than nothing.
    return readiness.summary ? (
      <div className="priorities priorities-ready">
        <span className="priorities-summary">✓ {readiness.summary}</span>
      </div>
    ) : null
  }
  return (
    <div className="priorities">
      <div className="priorities-head">
        <span className="priorities-title">Top priorities</span>
        {readiness.summary ? <span className="priorities-summary">{readiness.summary}</span> : null}
      </div>
      <ol className="priorities-list">
        {readiness.actions.map((a, i) => {
          const note = a.blocked ? a.blocked_why : a.why
          const lock = LOCKING[a.id]
          const asking = confirming === a.id
          return (
            <li
              key={a.id}
              className={`priority-card${a.recommended ? ' recommended' : ''}${a.blocked ? ' blocked' : ''}${asking ? ' asking' : ''}`}
            >
              <span className="priority-step">Step {i + 1}</span>
              <div className="priority-body">
                <span className="priority-label">{a.label}</span>
                {asking ? (
                  <span className="priority-why priority-locking">{lock.what}</span>
                ) : note ? (
                  <span className="priority-why">{note}</span>
                ) : null}
                {/* A queued step that just looks dimmer reads as arbitrary. Say
                    what it is waiting for. */}
                {!asking && !a.recommended && !a.blocked && i > 0 ? (
                  <span className="priority-after">after step {i}</span>
                ) : null}
                {/* The receipts. `why` now carries real counts ("4 answers and
                    2 changes since this was last structured"), and this is the
                    evidence behind them — the revision log's own wording, not
                    generated prose. Collapsed, because the count is the answer
                    to "should I press this" and the list is the answer to "why
                    do you say so". Absent when the backend could not read the
                    log honestly; it does not guess. */}
                {!asking && a.changes?.length ? (
                  <details className="priority-changes">
                    <summary>what changed</summary>
                    <ul>
                      {a.changes.map((c, k) => (
                        <li key={k} data-kind={c.kind}>
                          <span>{c.text}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </div>
              {asking ? (
                <span className="priority-confirm">
                  <button
                    className="priority-cta"
                    disabled={busy}
                    onClick={() => {
                      setConfirming(null)
                      setLaunched(a.id)
                      onAction(a)
                    }}
                  >
                    {lock.verb}
                  </button>
                  <button className="priority-cancel" onClick={() => setConfirming(null)}>
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  className={`priority-cta${busy && launched === a.id ? ' working' : ''}`}
                  disabled={busy || a.blocked || !a.available}
                  title={a.blocked ? a.blocked_why : undefined}
                  aria-busy={busy && launched === a.id}
                  onClick={() => {
                    if (lock) {
                      setConfirming(a.id)
                      return
                    }
                    setLaunched(a.id)
                    onAction(a)
                  }}
                >
                  {busy && launched === a.id ? 'Working…' : a.cta}
                  <span className="cta-arrow" aria-hidden>
                    {busy && launched === a.id ? '' : '→'}
                  </span>
                </button>
              )}
            </li>
          )
        })}
      </ol>
    </div>
  )
}
